import React, { createContext, useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUser } from "@/hooks/useUser";
import { useDocument } from "@/hooks/useDocument";
import { ICompasAIChatAction } from "@/types/compass.types";

interface CompassSocketContextType {
  taskStatuses: Record<string, string>;
  activeProcessingTask: ICompasAIChatAction | null;
  isConnected: boolean;
}

const CompassSocketContext = createContext<CompassSocketContextType | undefined>(undefined);

interface TaskUpdateResult {
  currentStatuses: Record<string, string>;
  hasCompletion: boolean;
  completedCount: number;
  processingTasks: ICompasAIChatAction[];
  wasProcessing: boolean;
  hadProcessingBefore: boolean;
}

const ACTIVE_STATUSES = ["queued", "processing", "pending"];
const COMPLETION_STATUSES = ["processed", "completed", "failed"];

function processTaskUpdate(
  tasks: ICompasAIChatAction[],
  previousStatuses: Record<string, string>
): TaskUpdateResult {
  const currentStatuses = { ...previousStatuses };
  const wasProcessing = Object.values(previousStatuses).some((s) => ACTIVE_STATUSES.includes(s));
  const hadProcessingBefore = Object.values(previousStatuses).some((s) => s === "processing");

  let hasCompletion = false;
  let completedCount = 0;
  const processingTasks: ICompasAIChatAction[] = [];

  tasks.forEach((task) => {
    const key = task.queue_item_id ?? task.id;
    if (key && task.status) {
      const previousStatus = currentStatuses[key];
      if (previousStatus === "processing" && task.status === "completed") {
        completedCount++;
      }
      currentStatuses[key] = task.status;
      if (
        (previousStatus == null || !COMPLETION_STATUSES.includes(previousStatus)) &&
        COMPLETION_STATUSES.includes(task.status)
      ) {
        hasCompletion = true;
      }
      if (task.status === "processing") {
        processingTasks.push(task);
      }
    }
  });

  return {
    currentStatuses,
    hasCompletion,
    completedCount,
    processingTasks,
    wasProcessing,
    hadProcessingBefore,
  };
}

function buildSocketUrl(userId: number | string, contextId: string): string {
  const apiUrl = process.env.API_BASE_URL ?? "http://localhost:8000";
  const url = new URL(apiUrl);
  const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
  const socketUrl = new URL(`${wsProtocol}//${url.host}/compas-ai/ws/queue-status`);
  socketUrl.searchParams.set("user_id", String(userId));
  socketUrl.searchParams.set("context_id", String(contextId));
  return socketUrl.toString();
}

function handleSocketMessage(
  event: MessageEvent,
  taskStatusesRef: React.MutableRefObject<Record<string, string>>,
  invalidationTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  callbacksRef: React.MutableRefObject<{ onUpdate?: () => void; onMessage?: () => void }>,
  setTaskStatuses: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setActiveProcessingTask: React.Dispatch<React.SetStateAction<ICompasAIChatAction | null>>,
  queryClient: ReturnType<typeof useQueryClient>,
  docId: string
): void {
  try {
    const parsed: unknown = JSON.parse(event.data);
    if (typeof parsed !== "object" || parsed == null || !("type" in parsed) || !("tasks" in parsed))
      return;
    const message = parsed as { type: string; tasks: ICompasAIChatAction[] };
    if (message.type !== "update" || message.tasks.length === 0) return;

    const result = processTaskUpdate(message.tasks, taskStatusesRef.current);

    if (result.completedCount > 0) {
      toast.success(
        result.completedCount > 1
          ? `${result.completedCount} actions completed`
          : "Action completed",
        { duration: 2000 }
      );
    }

    taskStatusesRef.current = result.currentStatuses;
    setTaskStatuses(result.currentStatuses);

    const hasActiveTasks = Object.values(result.currentStatuses).some((s) =>
      ACTIVE_STATUSES.includes(s)
    );

    if (result.hasCompletion) {
      if (invalidationTimeoutRef.current) {
        clearTimeout(invalidationTimeoutRef.current);
      }
      invalidationTimeoutRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["chatHistory", docId] });
        queryClient.invalidateQueries({ queryKey: ["compass-chat-history", docId] });
        const currentHasActive = Object.values(taskStatusesRef.current).some((s) =>
          ACTIVE_STATUSES.includes(s)
        );
        if (!currentHasActive && result.wasProcessing) {
          callbacksRef.current.onUpdate?.();
        }
        if (!currentHasActive) {
          setActiveProcessingTask(null);
        }
      }, 500);
    }

    if (result.processingTasks.length > 0) {
      const nextProcessingTask = result.processingTasks[0];
      setActiveProcessingTask(nextProcessingTask ?? null);
      // Fire on every processing update, not just the first transition.
      // Consumers must be idempotent (e.g. setHasUnreadDiscuss(true) is safe
      // to call repeatedly). The old `!hadProcessingBefore` guard caused a
      // race: if the user switched tabs AFTER the first "processing" WS
      // message, no further onMessage calls fired → unread badge never set.
      callbacksRef.current.onMessage?.();
    } else if (!hasActiveTasks) {
      setActiveProcessingTask(null);
    }
  } catch (error) {
    console.error("Error parsing socket message:", error);
  }
}

function useCompassConnection(
  onUpdate?: () => void,
  onMessage?: () => void
): CompassSocketContextType {
  const [taskStatuses, setTaskStatuses] = useState<Record<string, string>>({});
  const [activeProcessingTask, setActiveProcessingTask] = useState<ICompasAIChatAction | null>(
    null
  );
  const taskStatusesRef = React.useRef<Record<string, string>>({});
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { data: doc } = useDocument();
  const callbacksRef = React.useRef({ onUpdate, onMessage });
  const invalidationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbacksRef.current = { onUpdate, onMessage };
  }, [onUpdate, onMessage]);

  useEffect(() => {
    if (!user || doc?._id == null) return;

    let disposed = false;
    let retryDelay = 1000;
    let socket: WebSocket | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    const docId = doc._id;

    const connect = () => {
      if (disposed) return;
      socket = new WebSocket(buildSocketUrl(user.id, docId));

      socket.onopen = () => {
        setIsConnected(true);
        retryDelay = 1000;
      };

      socket.onmessage = (event) => {
        handleSocketMessage(
          event,
          taskStatusesRef,
          invalidationTimeoutRef,
          callbacksRef,
          setTaskStatuses,
          setActiveProcessingTask,
          queryClient,
          docId
        );
      };

      socket.onerror = () => setIsConnected(false);

      socket.onclose = () => {
        setIsConnected(false);
        if (!disposed) {
          retryTimeout = setTimeout(() => {
            if (!disposed) connect();
          }, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (invalidationTimeoutRef.current) clearTimeout(invalidationTimeoutRef.current);
      socket?.close();
      taskStatusesRef.current = {};
      setTaskStatuses({});
      setActiveProcessingTask(null);
    };
  }, [user, doc?._id, queryClient]);

  return { taskStatuses, activeProcessingTask, isConnected };
}

export const CompassSocketProvider: React.FC<{
  children: React.ReactNode;
  onUpdate?: () => void;
  onMessage?: () => void;
}> = ({ children, onUpdate, onMessage }) => {
  const value = useCompassConnection(onUpdate, onMessage);

  return <CompassSocketContext.Provider value={value}>{children}</CompassSocketContext.Provider>;
};

export const useCompassSocketContext = () => {
  const context = useContext(CompassSocketContext);
  if (context === undefined) {
    throw new Error("useCompassSocketContext must be used within a CompassSocketProvider");
  }
  return context;
};
